import { useMemo } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { ProjectId, User, UserId } from '@vd/types';
import { useAuth } from '../auth/AuthProvider';

/**
 * Every board list of a project, whatever its type filter: the prefix of
 * `boardTicketsKey`, for writing to all of them at once. The archive's list and
 * the ticket page's prev/next list are also under `['tickets', projectId]`, so an
 * invalidation of that still reaches everything, but they sit outside this prefix
 * and a board list's updates are never written into them.
 */
export const boardTicketListsKey = (projectId: string | undefined) => ['tickets', projectId, 'board'] as const;

/** The one board list for a type filter, as `typeKeyOf` names it. */
export const boardTicketsKey = (projectId: string | undefined, typeKey: string) =>
  [...boardTicketListsKey(projectId), typeKey] as const;

/**
 * Every ticket list of a project under `['tickets', projectId]`: the board's,
 * the archive's and the ticket page's prev/next list.
 */
export const projectTicketListsKey = (projectId: string | undefined) => ['tickets', projectId] as const;

/** The board's search results for a project: the prefix of `projectTicketSearchKey`. */
export const projectTicketSearchesKey = (projectId: string | undefined) => ['ticket-search', projectId] as const;

/** One search of a project's tickets, as the board asks it. */
export const projectTicketSearchKey = (projectId: string | undefined, query: string, archived: boolean | undefined) =>
  [...projectTicketSearchesKey(projectId), query, archived] as const;

/** Whether `key` starts with `prefix`, element by element. */
export const keyStartsWith = (key: QueryKey, prefix: QueryKey) => prefix.every((part, index) => key[index] === part);

/** Whether a key under `boardTicketListsKey` holds a type-filtered list. */
export const isTypeFilteredList = (key: QueryKey) => key[3] !== '';

/** A type filter as a key segment: sorted and joined, and '' for none. */
const typeKeyOf = (typeIds: readonly string[] | undefined) => (typeIds?.length ? [...typeIds].sort().join(',') : '');

/**
 * Bundle of board-page data + derived state.
 *
 * Earlier this hook was a thin wrapper around four React Query calls
 * — it didn't actually save any code over inlining at the one call
 * site. Now it also returns the `usersById` map every board view
 * re-derives from `members.data` (creator avatars, assignee chips,
 * comment authors), so the hook earns its keep. Keeping this map in
 * one place also means a future view that wants to look up a user by
 * id doesn't need to remember the `members.data?.forEach(({user}))`
 * shape.
 */
export function useBoardData(projectId: ProjectId | undefined, typeIds?: readonly string[]) {
  const { currentCompanyId } = useAuth();

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId!),
    enabled: !!projectId,
  });
  const columns = useQuery({
    queryKey: ['columns', projectId],
    queryFn: () => api.listColumns(projectId!),
    enabled: !!projectId,
  });
  /**
   * The type filter is part of the query KEY, not just the request (TOADO-411).
   *
   * Because it is applied server-side, two different filters are two different
   * result sets, and sharing one cache entry between them would show the previous
   * filter's tickets for a frame on every change. Sorted and joined so the key is
   * stable against the order somebody happened to tick the boxes in.
   */
  const typeKey = typeKeyOf(typeIds);
  const tickets = useQuery({
    queryKey: boardTicketsKey(projectId, typeKey),
    queryFn: () => api.listTickets(projectId!, undefined, typeIds).then((r) => r.items),
    enabled: !!projectId,
  });
  /**
   * The project's resolved type set: the company's, with this project's renames
   * and hidden types already applied by the server.
   */
  const ticketTypes = useQuery({
    queryKey: ['ticketTypes', projectId],
    queryFn: () => api.listProjectTicketTypes(projectId!).then((r) => r.items),
    enabled: !!projectId,
  });
  // Members query is keyed on the current company so a company switch
  // re-fetches cleanly instead of showing the previous tenant's roster.
  // Previously hardcoded to 'co-acme' — broken for any non-seeded company.
  const members = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  // Lookup by UserId — used as both `creatorsById` and `membersById`
  // by the board views (a creator IS a member of the workspace; the
  // names diverged historically when membership and authorship were
  // tracked separately, but they're served by the same Map today).
  const usersById = useMemo(() => {
    const m = new Map<UserId, User>();
    members.data?.forEach(({ user }) => m.set(user.id, user));
    return m;
  }, [members.data]);

  return { project, columns, tickets, members, usersById, ticketTypes };
}
